import { render } from 'react-dom';
import { renderEllipse } from '../live-draw/svg/Ellipse';
import { renderLine } from '../live-draw/svg/Line';
import { renderRect } from '../live-draw/svg/Rect';
import { renderText } from '../live-draw/svg/Text';
import { renderPath } from '../live-draw/svg/Path';
import { tryParseFloat } from '../util/NumberUtil';
import { newId } from '../util/DOMUtil';
import AnnotationUtil from '../queries/annotations/AnnotationUtil';
import { AnnotationAttachment } from '../autogenerated/ZoomBridge'; // eslint-disable-line

const renderFns = {
  ellipse: renderEllipse,
  rect: renderRect,
  line: renderLine,
  text: renderText,
  path: renderPath
};

const shapeDefaults = {
  strokeWidth: 5,
  stroke: 'red',
  fill: 'none',
};

export function svgToJson(data, creator) {
  const doc = new DOMParser().parseFromString(data, 'text/html');
  const node = doc.body.firstChild;
  const { dataset } = node;

  let result;

  // Check for old model.
  if (Object.keys(dataset).length === 0) {
    result = { ...shapeDefaults, creator };
    result.type = node.nodeName.toLowerCase();

    const attributes = [ ...node.getAttributeNames() ];
    attributes.forEach(key => {
      result[key] = tryParseFloat(node.getAttribute(key));
    });

    if (!result.id) {
      result.id = newId();
    }

    return result;
  }

  result = {};
  for (let key in dataset) {
    const val = dataset[key];
    result[key] = tryParseFloat(val);
  }

  return result;
}

export async function jsonToSvg(data) {
  const { type } = data;
  const renderFn = renderFns[type];
  const div = document.createElement('div');
  await new Promise(resolve => render(renderFn(data), div, resolve));

  const { dataset } = div.firstChild;
  delete dataset.reactroot;
  Object.assign(dataset, data);

  return div.innerHTML;
}

export async function updateAttachmentLocation(comment, newX, newY) {
  const shapes = AnnotationUtil.getAttachmentsFromThread(comment);
  if (!(shapes && shapes.length)) {
    return;
  }

  /** @type {AnnotationAttachment} */
  const shape = shapes[0];
  const svg = shape.getData();
  const json = svgToJson(svg, shape.getCreator());

  switch (json.type.toLowerCase()) {
    case 'rect':
      json.x = newX - json.width / 2;
      json.y = newY - json.height / 2;
      break;
    case 'ellipse':
      json.cx = newX;
      json.cy = newY;
      break;
    case 'line':
      const width = Math.abs(json.x2 - json.x1);
      const height = Math.abs(json.y2 - json.y1);
      json.x1 = newX - width / 2;
      json.x2 = newX + width / 2;
      json.y1 = newY - height / 2;
      json.y2 = newY + height / 2;
      break;
  }

  const newSvg = await jsonToSvg(json);
  shape.setData(newSvg);
}

export function updateAttachmentOffset(comment, shape) {
  const spatialLocation = AnnotationUtil.getSpatialLocation(comment);
  if (!spatialLocation) {
    return;
  }

  let x, y;
  switch (shape.type.toLowerCase()) {
    case 'rect':
      x = shape.x + shape.width / 2;
      y = shape.y + shape.height / 2;
      break;
    case 'ellipse':
      x = shape.cx;
      y = shape.cy;
      break;
    case 'line':
      const width = Math.abs(shape.x2 - shape.x1);
      const height = Math.abs(shape.y2 - shape.y1);
      x = Math.min(shape.x1, shape.x2) + width / 2;
      y = Math.min(shape.y1, shape.y2) + height / 2;
      break;
  }

  spatialLocation.setWidthOffset(x);
  spatialLocation.setHeightOffset(y);
}
